import axios from 'axios'
import { orderBy } from 'lodash'
import { eachDay, format } from 'date-fns'

import config from '../../config.json'
const API_SOURCE = `${config.backend_server_url}_api/schedule/`

const smallestDate = (date1, date2) => {
  if (!date1) {
    return date2
  }

  const num1 = Number(date1.split('-').join(''))
  const num2 = Number(date2.split('-').join(''))

  return num1 > num2 ? date2 : date1
}

const largestDate = (date1, date2) => {
  if (!date1) {
    return date2
  }

  const num1 = Number(date1.split('-').join(''))
  const num2 = Number(date2.split('-').join(''))

  return num1 < num2 ? date2 : date1
}


// Function for fetching port-calls from a vessel schedule.
const getPortCallsForDate = (vesselSchedule, date) => {
  for (let schedule of vesselSchedule) {
    if (schedule.date === date) {
      return schedule.port_calls
    }
  }
  return []
}

export default async function getScheduleTableV2() {
  /**
   * This method fetches the vessels and the schedule table from the API.
   * 
   * It then constructs a `scheduleData` object that contains an array of vessels and 
   * array of each day within schedule containing the port-calls for each vessel.
   * 
   * The reason for returning an array of vessels AND an array of dates is because we use the 
   * vessel-array to generate the header, and the date-array to generate the port-call entries 
   * within each date.
   * 
   * The vessel-array order is authorative so the vessel port-calls within the date-array are in the same 
   * order as the vessel-array.
   * 
   * Example returned object:
   *  {
   *   scheduleData: {
   *    boats: [
   *     {...vessel1},
   *     {...vessel2},
   *     ...
   *    ],
   *    days: [
   *     {
   *      date: '2023-01-01',
   *      data: [
   *       { ... , vessel1 portcalls: [ ... ] },
   *       { ... , vessel2 portcalls: [ ... ] },
   *       ...
   *      ]
   *     }
   *    ]
   *   }
   *  }
   */

  try {
    const response = await axios
      .all([
        axios.get(`${API_SOURCE}v2/schedule-table/?format=json`),
        axios.get(`${API_SOURCE}v2/vessels/?format=json`),
      ])
      .then(
        axios.spread(({ data: table }, { data: vessels }) => {
          // Map only boats that are available in the schedule table.
          // Also, add route_name from the first port_call in the vessel's schedule (if available)
          const boats = orderBy(
            Object.values(table).map(vessel => {
              const vesselInfo = vessels.find(
                ({ imo }) => `${imo}` === `${vessel.vessel_imo}`,
              )
              if (!vesselInfo) return undefined
              // Try to get route_name from the first port_call in the schedule
              let route_name = undefined
              if (Array.isArray(vessel.schedule) && vessel.schedule.length > 0) {
                for (const day of vessel.schedule) {
                  if (Array.isArray(day.port_calls) && day.port_calls.length > 0) {
                    route_name = day.port_calls[0].route_name
                    break
                  }
                }
              }
              return { ...vesselInfo, route_name }
            }),
            ['route_name', 'name', 'sortkey'],
          ).filter(item => item !== undefined)

          if (!table || !vessels) {
            return { error: true }
          }

          // Get the first and last day from the schedule-table data.
          const { min, max } = Object.values(table).reduce((results, item) => {
            const dates = item.schedule.map(
              vesselSchedule => vesselSchedule.date,
            )
            const min = dates[0]
            const max = dates[dates.length - 1]

            return {
              min: smallestDate(results.min, min),
              max: largestDate(results.max, max),
            }
          }, {})

          // Loop through and make the schedule-table by generating all dates from `min` 
          // to `max` date with all vessel port-calls for each date.
          // Within each date is an array of vessels and 
          // their port-calls. The array of vessels is in the same order as the 'boats' 
          // array generated above.
          const days = eachDay(new Date(min), new Date(max)).map(date => {
            const data = boats.map(({ imo, route_name }) => {
              let vesselData = []
              const vesselSchedule = Object.values(table).find(
                vesselObj => vesselObj.vessel_imo.toString() === imo,
              )

              if (vesselSchedule) {
                let portCalls = getPortCallsForDate(
                  vesselSchedule.schedule,
                  format(date, 'YYYY-MM-DD'),
                )
                if (portCalls.length > 0) {
                  for (let portCall of portCalls) {
                    vesselData.push({
                      voyage: portCall.voyage,
                      port: portCall.port_name,
                      portCode: portCall.port_locode,
                      eta: portCall.eta,
                      ets: portCall.etd,
                      closingList: Array.isArray(portCall.closing)
                        ? portCall.closing
                        : [],
                      'route_name': route_name || 'No route'
                    })
                  }
                }
              }
              return { vesselData }
            })

            return {
              date,
              data,
            }
          })

          return {
            scheduleData: { boats, days },
            updated: null,
         }
        }),
      )
      .catch(error => {
        return { error: error.response.status }
      })

    return response
  } catch (error) {
    return { error: true }
  }
}
